import { ViewDefnState, TenantConfigViewData, TenantViewDefnQuery } from 'src/dao/tenantConfigClient';
import { FavoriteListItemStorage, FavoriteResponseItem } from 'src/components/Subheader/Favorites/Favorites.types';
import { ViewDataState } from 'src/types/Domain';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isNil } from 'lodash';
import serviceContainer from 'src/ServiceContainer';
import { BasicPivotItem, ListDataOptions } from 'src/worker/pivotWorker.types';
import { cacheCheckFetchPivotData } from '../../StyleColorReview/StyleColorReview.slice';
import { ComponentErrorType, ErrorBoundaryComponentError } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { CategorySummaryComponent, ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { AppThunkDispatch, AppState } from 'src/store';
import { ASSORTMENT } from 'src/utils/Domain/Constants';
import { AnyAction as BaseAction } from 'redux';
import { getLocalConfig } from 'src/components/Subheader/Favorites/Favorites.utils';
import { overloadSubheader } from 'src/components/Subheader/Subheader.slice';
import {
  zChoiceProductivityViewDefn,
  zGroupByConfig,
  zKeyFinancialsViewDefn,
} from 'configuration-schemas/viewdefns/general';
import { zNestedGridViewDefn } from 'configuration-schemas/viewdefns/viewdefn';

export interface ViewDefns {
  groupByDefn: TenantConfigViewData;
  keyFinancials: TenantConfigViewData;
  choiceProductivity: TenantConfigViewData;
  gridDefn: TenantConfigViewData;
  unmodifiedViewDefn: TenantConfigViewData;
  favoritesList?: FavoriteResponseItem[];
}

export interface CategorySummaryViewData {
  keyFinancials: BasicPivotItem[];
  choiceProductivity: BasicPivotItem[];
  gridData: BasicPivotItem[];
}

export interface CategorySummarySlice {
  cacheHash: string | null;
  viewDataState: ViewDataState;
  viewDefnState: ViewDefnState;
  viewDefns?: ViewDefns;
}

const initialState: CategorySummarySlice = {
  cacheHash: null,
  viewDataState: ViewDataState.idle,
  viewDefnState: ViewDefnState.idle,
};

const categorySummaryReducer = createSlice({
  name: 'CategorySummary',
  initialState,
  reducers: {
    requestCategorySummaryViewDefns(state) {
      state.viewDefnState = ViewDefnState.loading;
    },
    receiveCategorySummaryViewDefns(state, action: PayloadAction<ViewDefns>) {
      state.viewDefnState = ViewDefnState.loaded;
      state.viewDefns = action.payload;
    },
    receiveGridViewDefn(state, action: PayloadAction<TenantConfigViewData>) {
      if (!isNil(state.viewDefns)) {
        state.viewDefns = {
          ...state.viewDefns,
          gridDefn: action.payload,
        };
      }
    },
    requestData(state) {
      state.viewDataState = ViewDataState.liveDataLoadingNoCache;
    },
    receiveCacheHash(state, action: PayloadAction<string>) {
      state.cacheHash = action.payload;
    },
    receiveCachedData(state, action: PayloadAction<string>) {
      // Ignore receipts from loads unrelated to current fetch.
      // (This could entirely be replaced with an epic for all screens using this technique.)
      if (action.payload === state.cacheHash) {
        state.viewDataState = ViewDataState.liveDataLoadingFoundCache;
      }
    },
    receiveLiveData(state, action: PayloadAction<string>) {
      if (action.payload === state.cacheHash) {
        state.viewDataState = ViewDataState.liveDataReady;
      }
    },
    cleanUp() {
      return initialState;
    },
    receiveError(_state, _action: PayloadAction<ErrorBoundaryComponentError>) {
      return initialState;
    },
  },
});

export const {
  requestCategorySummaryViewDefns,
  receiveCategorySummaryViewDefns,
  receiveGridViewDefn,
  requestData,
  receiveCacheHash,
  receiveCachedData,
  receiveLiveData,
  cleanUp,
  receiveError,
} = categorySummaryReducer.actions;

export function fetchCategorySummaryData(modelDefn: string, options: ListDataOptions) {
  return cacheCheckFetchPivotData(
    serviceContainer.pivotService.listDataCacheCheck(modelDefn, options),
    requestData,
    receiveCacheHash,
    receiveCachedData,
    receiveLiveData,
  );
}
export function fetchCategorySummaryConfigs(defns: CategorySummaryComponent['componentProps']['defns']) {
  return async (dispatch: AppThunkDispatch, _getState: () => AppState): Promise<BaseAction | void> => {
    const tenantParams: TenantViewDefnQuery = {
      defnIds: defns.view,
      appName: ASSORTMENT,
      favoritesDefn: defns.view[3],
      validationSchemas: [zGroupByConfig, zKeyFinancialsViewDefn, zChoiceProductivityViewDefn, zNestedGridViewDefn],
    };

    try {
      const configs = await serviceContainer.tenantConfigClient.getTenantViewDefnsWithFavorites(tenantParams);
      const defaultGroupByConf = configs[0];
      const unmodifiedViewDefn = configs[3];
      dispatch(
        overloadSubheader({
          groupByOptions: defaultGroupByConf,
          showSearch: true,
        }),
      );
      const localConfig: FavoriteListItemStorage | undefined = getLocalConfig(
        defns.view[3],
        (configs as any)[defns.view.length],
        dispatch,
        unmodifiedViewDefn,
      );
      if (localConfig && localConfig.config) {
        configs[3] = localConfig.config;
      }

      dispatch(
        receiveCategorySummaryViewDefns({
          groupByDefn: defaultGroupByConf,
          keyFinancials: configs[1],
          choiceProductivity: configs[2],
          gridDefn: configs[3],
          unmodifiedViewDefn,
        }),
      );
    } catch (error: any) {
      dispatch(
        receiveError({
          type: ComponentErrorType.config,
          message: error?.message || 'Unknown error',
          name: ConfDefnComponentType.categorySummary,
          issues: error,
          defnId: error?.defnId,
        }),
      );
    }
  };
}

export default categorySummaryReducer.reducer;
